/**
 * =============================================================================
 * PATIENT JOURNEY - VERTICAL STEPPER
 * =============================================================================
 *
 * Lays out the workflow as a vertical timeline:
 *
 *   1. every committed step, locked and visible
 *   2. the one active step, the only card that accepts input
 *   3. the stages still ahead, as locked placeholders with no data
 *   4. the closing banner once the case has reached a terminal state
 *
 * Two layouts, same cards and same workflow:
 *
 *   "plain" - the case details tab, paired with the horizontal progress bar
 *   "rail"  - the Close Case dialog, with a numbered rail down the left
 * =============================================================================
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CheckIcon from "@mui/icons-material/Check";

import JourneyStepCard, { STEP_CARD_VARIANT } from "./JourneyStepCard";
import JourneyStepBody from "./JourneyStepBody";

import { getStepLabel } from "./journeyConfig";
import { FONT, JOURNEY_COLORS } from "./journeyStyles";

export const JOURNEY_LAYOUT = {
    PLAIN: "plain",
    RAIL: "rail",
};

/* -----------------------------------------------------------------------------
   RAIL MARKER
----------------------------------------------------------------------------- */

const RailMarker = ({ number, variant, isLast }) => {
    const isCompleted = variant === STEP_CARD_VARIANT.COMPLETED;
    const isActive = variant === STEP_CARD_VARIANT.ACTIVE;

    return (
        <Box
            sx={{
                width: { xs: "26px", sm: "34px" },
                minWidth: { xs: "26px", sm: "34px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                alignSelf: "stretch",
                paddingTop: { xs: "12px", sm: "14px" },
            }}
        >
            <Box
                sx={{
                    width: { xs: "22px", sm: "26px" },
                    height: { xs: "22px", sm: "26px" },
                    minHeight: { xs: "22px", sm: "26px" },
                    borderRadius: "50%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isCompleted
                        ? JOURNEY_COLORS.success
                        : JOURNEY_COLORS.surface,
                    border: isCompleted
                        ? "1px solid " + JOURNEY_COLORS.success
                        : isActive
                            ? "1.5px solid " + JOURNEY_COLORS.accent
                            : "1px solid " + JOURNEY_COLORS.line,
                }}
            >
                {isCompleted ? (
                    <CheckIcon sx={{ fontSize: "15px", color: "#FFFFFF" }} />
                ) : (
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "12px",
                            lineHeight: "16px",
                            fontWeight: 600,
                            color: isActive
                                ? JOURNEY_COLORS.accent
                                : JOURNEY_COLORS.textMuted,
                        }}
                    >
                        {number}
                    </Typography>
                )}
            </Box>

            {!isLast && (
                <Box
                    sx={{
                        flex: 1,
                        width: "2px",
                        minHeight: "16px",
                        backgroundColor:
                            variant === STEP_CARD_VARIANT.COMPLETED
                                ? "#348530"
                                : JOURNEY_COLORS.border,
                    }}
                />
            )}
        </Box>
    );
};

/* -----------------------------------------------------------------------------
   CLOSED BANNER
----------------------------------------------------------------------------- */

const ClosedBanner = ({ entry }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: { xs: "12px", sm: "14px" },
            marginTop: "4px",
            borderRadius: "8px",
            border: "1px solid " + JOURNEY_COLORS.success,
            backgroundColor: JOURNEY_COLORS.successSoft,
            boxSizing: "border-box",
        }}
    >
        <TaskAltIcon
            sx={{
                fontSize: "20px",
                color: JOURNEY_COLORS.success,
                flexShrink: 0,
            }}
        />

        <Box>
            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: "13px",
                    lineHeight: "18px",
                    fontWeight: 700,
                    color: JOURNEY_COLORS.success,
                }}
            >
                Patient journey closed
            </Typography>

            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: JOURNEY_COLORS.text,
                    marginTop: "2px",
                }}
            >
                {entry?.outcomeLabel
                    ? "Final outcome: " + entry.outcomeLabel + "."
                    : "The final details have been recorded."}{" "}
                No further steps can be recorded for this case.
            </Typography>
        </Box>
    </Box>
);

/* -----------------------------------------------------------------------------
   COMPONENT
----------------------------------------------------------------------------- */

const JourneySections = ({
    workflow,
    documents = [],
    isSaving = false,
    showUpcomingSteps = true,
    hiddenNodeIds = [],
    layout = JOURNEY_LAYOUT.PLAIN,
    onDocumentDownload,
    onSubmitStep,
}) => {
    if (!workflow) {
        return null;
    }

    const {
        entries,
        currentNode,
        currentStage,
        isClosed,
        closingEntry,
        upcomingStages,
        draft,
        errors,
        minTimestamp,
        willCloseCase,
        canSubmit,
        pendingRequirement,
        selectOption,
        setTimestamp,
        setFieldValue,
        setAcknowledged,
        submitStep,
    } = workflow;

    /* -------------------------------------------------------------------------
       ROWS - one per card, in the order they are stacked
    ------------------------------------------------------------------------- */

    const rows = [];

    entries.forEach((entry, index) => {
        /* The dialog records the acknowledgment before the numbered rail
           starts, so it is not shown as one of those steps. */
        if (hiddenNodeIds.includes(entry.nodeId)) {
            return;
        }

        rows.push({
            key: entry.nodeId + "-" + index,
            variant: STEP_CARD_VARIANT.COMPLETED,
            number: entry.stage,
            stepLabel: entry.stepLabel,
            title: entry.title,
            location: entry.location,
            entry,
        });
    });

    if (!isClosed && currentNode) {
        rows.push({
            key: "active-" + currentNode.id,
            variant: STEP_CARD_VARIANT.ACTIVE,
            number: currentStage,
            stepLabel: getStepLabel(currentNode, currentStage),
            title: currentNode.title,
            location: currentNode.location,
            isCurrent: true,
        });
    }

    if (showUpcomingSteps && !isClosed) {
        upcomingStages.forEach((stage) => {
            rows.push({
                key: "upcoming-" + stage.id,
                variant: STEP_CARD_VARIANT.UPCOMING,
                number: stage.stage,
                stepLabel: "STEP " + stage.stage,
                title: stage.label,
                location: stage.location,
            });
        });
    }

    const isRail = layout === JOURNEY_LAYOUT.RAIL;

    /* -------------------------------------------------------------------------
       RENDER
    ------------------------------------------------------------------------- */

    return (
        <Box sx={{ width: "100%", fontFamily: FONT }}>
            {rows.map((row, index) => {
                const card = (
                    <JourneyStepCard
                        variant={row.variant}
                        stepLabel={row.stepLabel}
                        title={row.title}
                        location={row.location}
                        entry={row.entry}
                    >
                        {row.isCurrent && (
                            <JourneyStepBody
                                node={currentNode}
                                draft={draft}
                                errors={errors}
                                minTimestamp={minTimestamp}
                                willCloseCase={willCloseCase}
                                canSubmit={canSubmit}
                                pendingRequirement={pendingRequirement}
                                isSaving={isSaving}
                                documents={documents}
                                onSelectOption={selectOption}
                                onTimestampChange={setTimestamp}
                                onFieldChange={setFieldValue}
                                onAcknowledgedChange={setAcknowledged}
                                onDocumentDownload={onDocumentDownload}
                                onSubmit={onSubmitStep || submitStep}
                            />
                        )}
                    </JourneyStepCard>
                );

                if (!isRail) {
                    return (
                        <React.Fragment key={row.key}>{card}</React.Fragment>
                    );
                }

                return (
                    <Box
                        key={row.key}
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            gap: { xs: "8px", sm: "12px" },
                        }}
                    >
                        <RailMarker
                            number={row.number}
                            variant={row.variant}
                            isLast={index === rows.length - 1 && isClosed}
                        />

                        <Box sx={{ flex: 1, minWidth: 0 }}>{card}</Box>
                    </Box>
                );
            })}

            {/* ============ CLOSED ============ */}

            {isClosed && (
                <Box
                    sx={{
                        paddingLeft: isRail
                            ? { xs: "34px", sm: "46px" }
                            : 0,
                    }}
                >
                    <ClosedBanner entry={closingEntry} />
                </Box>
            )}
        </Box>
    );
};

export default JourneySections;
